package screens.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import design.AccessibilityGuidelines
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.clearSessionStorage
import utils.getSessionItem

/**
 * OIDC Callback Screen
 *
 * This screen handles the callback from the OIDC provider (Hydra).
 * It extracts the authorization code and state from URL parameters,
 * validates them, and exchanges the code for tokens.
 */
@Composable
fun OidcCallbackScreen(
    onLoginSuccess: () -> Unit,
    onNavigateToLogin: () -> Unit,
    authViewModel: AuthViewModel,
) {
    val state by authViewModel.state.collectAsState()
    var callbackProcessed by remember { mutableStateOf(false) }
    var processingError by remember { mutableStateOf<String?>(null) }

    // Extract URL parameters on mount
    LaunchedEffect(Unit) {
        if (callbackProcessed) return@LaunchedEffect

        try {
            // Get URL parameters from current window location
            val urlParams = getUrlParams()

            val code = urlParams["code"]
            val stateParam = urlParams["state"]
            val error = urlParams["error"]
            val errorDescription = urlParams["error_description"]

            when {
                // Handle error response from OIDC provider
                error != null -> {
                    val errorMessage =
                        when (error) {
                            "access_denied" -> "ログインがキャンセルされました"
                            "invalid_scope" -> "要求されたスコープは許可されていません"
                            "server_error" -> "認証サーバーエラーが発生しました。再度お試しください"
                            "temporarily_unavailable" -> "サービスが一時的に利用できません。しばらく待ってから再度お試してください"
                            else -> errorDescription ?: "認証エラーが発生しました: $error"
                        }
                    processingError = errorMessage
                    AccessibilityGuidelines.announceError(errorMessage)
                    callbackProcessed = true
                }
                // Handle successful authorization
                code != null && stateParam != null -> {
                    // Validate state against sessionStorage
                    val storedState = getSessionItem("oidc_state")
                    if (storedState != stateParam) {
                        processingError = "セッションが無効です。最初からやり直してください"
                        AccessibilityGuidelines.announceError(processingError!!)
                        callbackProcessed = true
                    } else {
                        // State is valid, proceed with callback
                        authViewModel.onEvent(AuthEvent.OnOidcCallbackReceived(code, stateParam, null))
                        // Clear sessionStorage after sending event
                        clearSessionStorage()
                        callbackProcessed = true
                    }
                }
                // Missing required parameters
                else -> {
                    processingError = "無効なコールバックURLです。認証フローを最初からやり直してください"
                    AccessibilityGuidelines.announceError(processingError!!)
                    callbackProcessed = true
                }
            }
        } catch (e: Exception) {
            processingError = "コールバック処理中にエラーが発生しました: ${e.message}"
            AccessibilityGuidelines.announceError(processingError!!)
            callbackProcessed = true
        }
    }

    // Navigate on successful login
    LaunchedEffect(state.isLoggedIn) {
        if (state.isLoggedIn) {
            delay(500)
            onLoginSuccess()
        }
    }

    // Announce errors to screen readers
    LaunchedEffect(processingError) {
        if (processingError != null) {
            AccessibilityGuidelines.announceError(processingError!!)
        }
    }
    LaunchedEffect(state.errorMessage) {
        if (state.errorMessage != null) {
            AccessibilityGuidelines.announceError(state.errorMessage!!)
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text("認証処理中")
            }

            P({
                style {
                    property("color", "#666")
                    property("margin-bottom", "20px")
                    property("font-size", "14px")
                    property("text-align", "center")
                }
            }) {
                when {
                    state.isLoading -> Text("認証情報を確認しています...")
                    processingError != null -> Text("認証に失敗しました")
                    state.errorMessage != null -> Text("エラーが発生しました")
                    else -> Text("処理中...")
                }
            }

            // Loading spinner
            if (state.isLoading) {
                Div({
                    style {
                        property("display", "flex")
                        property("justify-content", "center")
                        property("align-items", "center")
                        property("margin", "40px 0")
                    }
                }) {
                    Div({
                        style {
                            property("width", "40px")
                            property("height", "40px")
                            property("border", "4px solid #f3f3f3")
                            property("border-top", "4px solid #007bff")
                            property("border-radius", "50%")
                            property("animation", "spin 1s linear infinite")
                        }
                        attr("role", "status")
                        attr("aria-live", "polite")
                        attr("aria-label", "読み込み中")
                    }) {}
                }
            }

            // Error messages
            if (processingError != null) {
                Div({
                    classes(AppStyleSheet.formError)
                    style {
                        property("background-color", "#f8d7da")
                        property("border", "1px solid #f5c6cb")
                        property("color", "#721c24")
                        property("padding", "16px")
                        property("border-radius", "4px")
                        property("margin-bottom", "20px")
                        property("text-align", "center")
                    }
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(processingError!!)
                }
            }

            if (state.errorMessage != null && processingError == null) {
                Div({
                    classes(AppStyleSheet.formError)
                    style {
                        property("background-color", "#f8d7da")
                        property("border", "1px solid #f5c6cb")
                        property("color", "#721c24")
                        property("padding", "16px")
                        property("border-radius", "4px")
                        property("margin-bottom", "20px")
                        property("text-align", "center")
                    }
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(state.errorMessage!!)
                }
            }

            // Action buttons (shown only when not loading and error occurred)
            if (!state.isLoading && (processingError != null || state.errorMessage != null)) {
                Div({ classes(AppStyleSheet.loginButtonGroup) }) {
                    Button({
                        classes(AppStyleSheet.submitButton)
                        onClick { onNavigateToLogin() }
                        attr("aria-label", "ログイン画面に戻る")
                    }) {
                        Text("ログイン画面に戻る")
                    }
                }

                P({
                    style {
                        property("font-size", "12px")
                        property("color", "#666")
                        property("margin-top", "16px")
                        property("text-align", "center")
                    }
                }) {
                    Text("最初から認証フローをやり直してください")
                }
            }
        }
    }
}

/**
 * Helper function to extract URL parameters from current window location
 * @return Map of URL parameter keys to values
 */
private fun getUrlParams(): Map<String, String> {
    val params = mutableMapOf<String, String>()
    try {
        @Suppress("UnsafeDynamicallyTypedCall")
        val urlParams = js("new URLSearchParams(window.location.search)")
        @Suppress("UnsafeDynamicallyTypedCall")
        val entries = js("Array.from(urlParams.entries())") as Array<Array<String>>
        for ((key, value) in entries) {
            params[key] = value
        }
    } catch (e: Exception) {
        console.error("Failed to parse URL params:", e)
    }
    return params
}
